import {
    type IListPageChildProps,
    type IListPageLayoutProps,
    ListPageLayout,
} from "layouts/ListPageLayout";
import { createContext, type ReactNode, useContext, useState } from "react";
import { Route, type RouteComponentProps } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { BuiTokenScreenLg } from "@btnet/blueprint-design-tokens";
import { IconSideDrawer } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTShellLayout, type IShellNavItem } from "@btnet/core-components/BTShellLayout";
import { BTVerticalNav } from "@btnet/core-components/BTVerticalNav";

import { AppProvider } from "helpers/AppProvider";
import { AppProviderHandler } from "helpers/AppProvider.api.handler";
import { BTLinkSpaRoutingProvider } from "helpers/globalContext/BTLinkSpaRoutingProvider";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { DefaultEnabledSpaInfo, SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { getJobPickerState, getSelectedJobId } from "utilities/jobPicker/jobPicker";

import { BTLogo } from "commonComponents/btWrappers/BTLogo/BTLogo";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { InfoArchOptInToggle } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/InfoArchOptInToggle";
import { useInfoArchOptIn } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/useInfoArchOptIn";

/**
 * Which entities are available in research preview, and how the sidebar lists them
 * (ado-290557). Owned by the layout rather than fixture data shared with the generic
 * `BTShellLayout.stories.tsx` demo (`RESEARCH_PREVIEW_SHELL_NAV_ITEMS`, still 20 dummy items
 * for that story's own grouping showcase) — every route that opts into research preview shows
 * this real sidebar. Today it is only Bills; the list grows here as more entities are ported.
 */
const RESEARCH_PREVIEW_NAV_ITEMS: IShellNavItem[] = [
    { key: "bills", label: "Bills", group: "Financials" },
];

/** Matches ListPageLayout's module-level default so a route gets the same handler instance. */
const defaultAppProviderHandler = new AppProviderHandler();

/**
 * The per-page provider stack `ListPageLayout` adds around page content (see
 * `ListPageLayout.tsx`, `ListPageLayout`/`ListPageLayoutContext`). Wraps `BTShellLayout`'s
 * `children` canvas slot so the ported page picks up the same context it would under
 * `ListPageLayout`.
 */
const BTShellWrapper = ({
    children,
    appProviderHandler = defaultAppProviderHandler,
}: {
    children: ReactNode;
    appProviderHandler?: AppProviderHandler;
}) => (
    <AppProvider handler={appProviderHandler}>
        <FocusProvider shareFocusWithParent>
            <SpaInfoContext.Provider value={DefaultEnabledSpaInfo}>
                <BTLinkSpaRoutingProvider>{children}</BTLinkSpaRoutingProvider>
            </SpaInfoContext.Provider>
        </FocusProvider>
    </AppProvider>
);

interface IShellCanvasProps extends IListPageLayoutProps {
    matchProps: RouteComponentProps;
}

/**
 * Canvas slot: invokes the v1 page function exactly as `ListPageLayout` does, so a ported
 * page renders unchanged.
 *
 * Job selection always happens in the v1 job picker — a user picks a job outside
 * research-preview — so the shell has no picker UI and no picker load. It reads the user's
 * persisted selection via `getJobPickerState()` (which holds the real selected job ids, or the
 * full set for All-Jobs) and synthesizes `IListPageChildProps` synchronously.
 *
 * Simplifications vs. v1 (research-preview scope): the selected job's display name is not
 * resolved (a single job's `jobName` is empty; All-Jobs uses the route label), and a builder's
 * General job is not surfaced. If the user has no selection the page still renders and shows
 * its own "select a jobsite" empty state.
 */
const ShellCanvas = ({ matchProps, component, allJobsText }: IShellCanvasProps) => {
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    const builderId = builderInfo?.builderId ?? 0;

    // The v1 picker persists real job ids (single selection, or every selected job for
    // All-Jobs); filter out the AllJobs(0)/NoJobs(-1) sentinels.
    const { selectedJobIds, isAllJobsSelected, isTemplateMode } = getJobPickerState();
    let jobIds = selectedJobIds.filter((id) => id > 0);
    if (jobIds.length === 0) {
        // No picker selection — fall back to the remembered single jobsite selection.
        const selectedJobId = getSelectedJobId(builderId);
        if (selectedJobId > 0) {
            jobIds = [selectedJobId];
        }
    }

    const listLayoutProps: IListPageChildProps = {
        key: 0,
        builderId,
        jobIds,
        jobName: isAllJobsSelected ? (allJobsText ?? "All Jobs") : "",
        userId: userInfo?.globalUserId ?? 0,
        isTemplateMode,
        loginType: userInfo?.loginType ?? BTLoginTypes.BUILDER,
        // Selection is read synchronously from storage, so the page is ready immediately. An
        // empty selection still renders the page, which shows its "select a jobsite" state.
        jobPickerReady: true,
        isAllJobsSelected,
        jobPickerCount: jobIds.length,
        setJobPickerCollapsed: () => undefined,
    };

    return <>{component(listLayoutProps, matchProps)}</>;
};

interface IShellNavState {
    /** Whether a page should treat the left nav as present. True whenever the sidebar is open,
     *  and always true below the `lg` breakpoint — the sidebar never renders there, so there is
     *  nothing for a page to offer to reopen. Not a "is the sidebar visible" flag. */
    isLeftNavOpen: boolean;
    /** Reopen the left nav after the sidebar's own collapse button has closed it. */
    openLeftNav: () => void;
}

const ShellNavContext = createContext<IShellNavState | undefined>(undefined);

/**
 * The shell's left-nav state, for use inside a ported v1 page (e.g. `BillsInbox`). The
 * open/closed state lives in the shell — it's the shell that unmounts the sidebar when
 * collapsed — but the "reopen" affordance belongs in the page's own header, next to its title
 * (matching the reference prototype). This hook is how a page reads the state and wires up
 * that button. Throws outside the shell: a page is only ever rendered inside it while opted in.
 */
export function useShellNav(): IShellNavState {
    const value = useContext(ShellNavContext);
    if (value === undefined) {
        throw new Error("useShellNav must be used within the research-preview shell");
    }
    return value;
}

interface INavHeaderProps {
    onCollapse: () => void;
}

/** The sidebar's `top` slot — logo + collapse button, matching the reference prototype. */
const NavHeader = ({ onCollapse }: INavHeaderProps) => (
    <div className="flex items-center justify-between p-5 pb-0">
        <BTLogo width={18} />
        <BTButton
            type="tertiary"
            data-testid="nav-collapse"
            aria-label="Collapse"
            icon={<IconSideDrawer />}
            onClick={onCollapse}
        />
    </div>
);

/** Sidebar slot — real nav data wiring is a follow-up; the research-preview toggle is live. */
const ShellSidebar = ({
    navItems,
    selectedKey,
    onSelectKey,
    onCollapse,
}: {
    navItems: IShellNavItem[];
    selectedKey: string;
    onSelectKey: (key: string) => void;
    onCollapse: () => void;
}) => (
    <BTVerticalNav
        data-testid="research-preview-sidebar"
        navItems={navItems}
        selectedKey={selectedKey}
        onSelectKey={onSelectKey}
        top={<NavHeader onCollapse={onCollapse} />}
        bottom={
            <InfoArchOptInToggle
                label="Research preview"
                labelClassName="text-fg-secondary opacity-100"
                testIdPrefix="researchPreviewSidebarOptIn"
            />
        }
    />
);

/**
 * Research-preview (info-arch) per-route layout (ado-286232).
 *
 * Porting a v1 list page to the BTShellLayout is a one-line route change: swap
 * `<ListPageLayout .../>` for `<ResearchPreviewLayout .../>` with identical props. Opted-in
 * users (`userInfo.optIns.infoArch === true`) get the page rendered inside `BTShellLayout`'s canvas
 * slot; everyone else falls through to `ListPageLayout` unchanged, so each swap is independent
 * and safe. Routes not yet ported keep their `ListPageLayout` registration.
 */
export const ResearchPreviewLayout = (props: IListPageLayoutProps) => {
    const { isOptedIn } = useInfoArchOptIn();
    const belowLgBreakpoint = useMediaQuery(`(max-width: ${BuiTokenScreenLg})`);
    const [selectedNavKey, setSelectedNavKey] = useState(RESEARCH_PREVIEW_NAV_ITEMS[0].key);
    const [leftNavOpen, setLeftNavOpen] = useState(true);
    if (!isOptedIn) {
        return <ListPageLayout {...props} />;
    }

    // ListPageLayout registers the route internally; in shell mode we register it here so the
    // canvas can hand `matchProps` to the v1 page function (ShellCanvas). `component` is
    // excluded from the spread because it collides with Route's own `component` prop, which
    // would silently win over `render` — ShellCanvas reads it off `props` directly instead.
    const { component: _component, ...routeProps } = props;
    return (
        <Route
            {...routeProps}
            render={(matchProps) => (
                <BTShellWrapper appProviderHandler={props.appProviderHandler}>
                    <BTShellLayout
                        data-testid="research-preview-shell"
                        className="research-preview-shell"
                        sidebar={
                            !belowLgBreakpoint && leftNavOpen ? (
                                <ShellSidebar
                                    navItems={RESEARCH_PREVIEW_NAV_ITEMS}
                                    selectedKey={selectedNavKey}
                                    onSelectKey={setSelectedNavKey}
                                    onCollapse={() => setLeftNavOpen(false)}
                                />
                            ) : undefined
                        }
                        // No bottombar for now (ado-290557): this design has no horizontal nav,
                        // only vertical. BTShellLayout keeps the slot — pass one here once there
                        // are real mobile destinations.
                    >
                        <ShellNavContext.Provider
                            value={{
                                // Below `lg` there is no sidebar to reopen (it never renders
                                // regardless of `leftNavOpen`, and there's no bottombar fallback
                                // either) — report "open" there too so a page never offers a
                                // reopen affordance with nothing behind it, e.g. narrowing below
                                // lg after collapsing on desktop.
                                isLeftNavOpen: belowLgBreakpoint || leftNavOpen,
                                openLeftNav: () => setLeftNavOpen(true),
                            }}
                        >
                            <ShellCanvas {...props} matchProps={matchProps} />
                        </ShellNavContext.Provider>
                    </BTShellLayout>
                </BTShellWrapper>
            )}
        />
    );
};
